import { EnhancedCustomer } from "../../../../Models/Customer";
import LeadResultType from "../../../Constants/LeadResultType";
import DispositionScenario from "../Types/DispositionScenario";
import DispositionScenarioFilter, {
  ScenarioConditionalExclusion,
  ScenarioConditionalInclusion,
  ScenarioFilteredExclusion,
  ScenarioFilteredInclusion
} from "../Types/DispositionScenarioFilter";

/** A lead result type filter which always includes 'non-candidate' lead result type options. */
const IncludeDisqualifyingTypes = new ScenarioFilteredInclusion(
  () => true,
  ({ disqualifyingReasons }, options) => options.filter(o => disqualifyingReasons.some(r => r.leadResultTypeId == o.id))
);

/** A lead result type condition which excludes the 'NotReached' lead result type when the session contact was inbound.
 * @remarks This should be fully validated as correct before use. Inbound webform leads can still be 'Not Reached'.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ExcludeNotReachedOnInboundSession = new ScenarioConditionalExclusion(
  ({ lead: { outbound }, isLeadManualOutbound }) => Boolean(!outbound) && !isLeadManualOutbound,
  [LeadResultType.NotReached]
);
/** A lead result type condition which includes the 'NotReached' lead result type when the session contact was outbound.
 * @remarks This should be fully validated as correct before use. Inbound webform leads can still be 'Not Reached'.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const IncludeNotReachedOnOutboundSession = new ScenarioConditionalInclusion(
  ({ lead: { outbound }, isLeadManualOutbound }) => Boolean(outbound) || isLeadManualOutbound,
  [LeadResultType.NotReached]
);

/** A lead result type filter which prevents the 'Existing Patient' lead result type when the session's customer was loaded from previous session. */
const ExcludeExistingPatientOnNewCustomer = new ScenarioConditionalExclusion(
  ({ selectedCustomer }) => Boolean(!selectedCustomer),
  [LeadResultType.ExistingPatient]
);
/** A lead result type filter which prevents the 'Existing Patient' lead result type when the session's customer was loaded from previous session. */
const IncludeExistingPatientOnLoadedCustomer = new ScenarioConditionalInclusion(
  ({ selectedCustomer }) => Boolean(selectedCustomer),
  [LeadResultType.ExistingPatient]
);

/** A lead result type filter which always excludes 'non-candidate' lead result type options. */
const ExcludeDisqualifyingTypes = new ScenarioFilteredExclusion(
  () => true,
  ({ disqualifyingReasons }, options) => options.filter(o => disqualifyingReasons.some(r => r.leadResultTypeId == o.id))
);

/** The scenario filter that should be applied for scenarios that control 'negative' outcomes such as missed session
 * steps or missing required information.
 */
const InvalidSessionScenarioFilter = new DispositionScenarioFilter({
  staticInclusions: [
    LeadResultType.BadLead,
    LeadResultType.DoNotCall,
    LeadResultType.ExistingPatient,
    LeadResultType.NotReached,
    LeadResultType.NotReached,
    LeadResultType.DroppedChat
  ],
  conditionalExclusions: [ExcludeExistingPatientOnNewCustomer]
});

/** A scenario applicable when the customer has key properties which are identified as invalid for persistence. */
export const InvalidCustomerPropertiesScenario = new DispositionScenario(
  ({ customer }) => Boolean(EnhancedCustomer.getInvalidPrimaryInfoItems(customer).length),
  InvalidSessionScenarioFilter
);

/** A scenario applicable when the user has not visited any other steps than Search, Intro, and Disposition. */
export const ShortCircuitScenario = new DispositionScenario(
  ({ isSessionShortCircuited }) => isSessionShortCircuited,
  InvalidSessionScenarioFilter
);

/** The scenario applicable when the customer is not a candidate for services. */
export const CustomerDisqualifiedScenario = new DispositionScenario(
  ({ leadDisqualifiers }) => Boolean(leadDisqualifiers.length),
  new DispositionScenarioFilter({
    staticInclusions: [
      LeadResultType.BadLead,
      LeadResultType.OtherProcedures,
      LeadResultType.NotReached,
      LeadResultType.DroppedChat
    ],
    conditionalInclusions: [IncludeExistingPatientOnLoadedCustomer],
    filteredInclusions: [IncludeDisqualifyingTypes]
  })
);

/** The scenario applicable when the user has booked an appointment or confirmed that one was made externally. */
export const BookedAppointmentScenario = new DispositionScenario(
  ({ appointment, isBookingManuallyConfirmed }) => Boolean(appointment) || isBookingManuallyConfirmed,
  new DispositionScenarioFilter({ staticInclusions: [LeadResultType.Booked] })
);

/** The scenario when the customer was not disqualified or invalid, and an appointment was not
 * booked.
 * @remarks The scenario constraint should always be 'true' so this could be considered the 'default' scenario when
 * no other is applicable.
 */
export const NoBookScenario = new DispositionScenario(
  () => true,
  new DispositionScenarioFilter({
    staticExclusions: [LeadResultType.Booked],
    conditionalExclusions: [ExcludeExistingPatientOnNewCustomer],
    filteredExclusions: [ExcludeDisqualifyingTypes]
  })
);
